import {
  Box,
  Button,
  formatDateTime,
  FullPageSpinner,
  HStack,
  Progress,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
  WhiteCard,
} from '@elkaso-app/web-design';
import { useGetCreditFacilityApi } from 'apis/bnpl';
import { useEffect } from 'react';
import { changeTitle } from 'redux/actions';
import { useAppDispatch } from 'redux/hooks';
import { CreditEventDto } from 'apis/bnpl';
import { BnplStatusBadge } from 'pages/bnpl/components/bnpl-status-badge';
import { RecordRepaymentModal } from 'pages/bnpl/components/record-repayment-modal';
import { UpdateCreditLimitModal } from 'pages/bnpl/components/update-credit-limit-modal';
import { UpdateFacilityStatusModal } from 'pages/bnpl/components/update-facility-status-modal';
import { CreditEventType, creditEventTypeLabels, formatAmount } from 'pages/bnpl/utils/bnpl-statuses';
import { useHistory, useParams } from 'react-router-dom';

const InfoItem = ({ label, value, children }: { label: string; value?: string; children?: React.ReactNode }) => (
  <Box minW='150px'>
    <Text fontSize='xs' color='gray.500'>
      {label}
    </Text>
    {children || <Text fontWeight='medium'>{value || '---'}</Text>}
  </Box>
);

export const CreditFacilityDetailScreen = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { isLoading, data: facilityData } = useGetCreditFacilityApi(id);

  useEffect(() => {
    dispatch(changeTitle('BNPL - Credit Facility'));
  }, [dispatch]);
  const facility = facilityData?.data;

  if (isLoading) return <FullPageSpinner />;
  if (!facility) return <Text p='lg'>Facility not found</Text>;

  const utilizationPercent = facility.creditLimit > 0 ? (facility.utilizedAmount / facility.creditLimit) * 100 : 0;
  const reservedPercent = facility.creditLimit > 0 ? (facility.reservedAmount / facility.creditLimit) * 100 : 0;

  return (
    <VStack spacing='lg' align='stretch' p='lg'>
      {/* Summary Card */}
      <WhiteCard p='lg'>
        {/* Header row: title + action buttons */}
        <HStack justify='space-between' mb='lg'>
          <Text fontSize='xl' fontWeight='bold'>
            Credit Facility #{facility.id}
          </Text>
          <HStack spacing='sm'>
            <UpdateFacilityStatusModal
              trigger={
                <Button size='sm' variant='outline' colorScheme='red'>
                  Update Status
                </Button>
              }
              facilityId={facility.id}
              currentStatus={facility.facilityStatus}
            />
            <UpdateCreditLimitModal
              trigger={
                <Button size='sm' variant='outline' colorScheme='red'>
                  Update Limit
                </Button>
              }
              facilityId={facility.id}
              currentLimit={facility.creditLimit}
            />
            {facility.facilityStatus === 'active' && (
              <RecordRepaymentModal
                trigger={
                  <Button size='sm' variant='outline' colorScheme='green'>
                    Record Repayment
                  </Button>
                }
                facilityId={facility.id}
                utilizedAmount={facility.utilizedAmount}
              />
            )}
          </HStack>
        </HStack>

        {/* Info grid */}
        <HStack spacing='xl' flexWrap='wrap'>
          <InfoItem label='Restaurant' value={facility.restaurant?.nameEn} />
          <InfoItem label='Supplier' value={facility.supplier?.name} />
          <InfoItem label='Status'>
            <BnplStatusBadge status={facility.facilityStatus} type='facility' />
          </InfoItem>
          <InfoItem label='Credit Limit' value={formatAmount(facility.creditLimit)} />
          <InfoItem label='Utilized' value={formatAmount(facility.utilizedAmount)} />
          <InfoItem label='Reserved' value={formatAmount(facility.reservedAmount)} />
          <InfoItem label='Available' value={formatAmount(facility.availableAmount)} />
        </HStack>

        {/* Utilization progress bar */}
        <Box mt='lg'>
          <Text fontSize='sm' color='gray.500' mb='xs'>
            Utilization: {utilizationPercent.toFixed(1)}% used, {reservedPercent.toFixed(1)}% reserved
          </Text>
          <Progress value={utilizationPercent} colorScheme='red' borderRadius='full' size='sm' />
          <Text fontSize='xs' color='gray.500' mt='xs'>
            {formatAmount(facility.utilizedAmount)} utilized + {formatAmount(facility.reservedAmount)} reserved of{' '}
            {formatAmount(facility.creditLimit)} limit
          </Text>
        </Box>
      </WhiteCard>

      {/* Tabs: Events, Utilizations, Statements */}
      <WhiteCard p='lg'>
        <Tabs>
          <TabList>
            <Tab>Events</Tab>
            <Tab>Utilizations</Tab>
            <Tab>Statements</Tab>
          </TabList>
          <TabPanels>
            {/* Events Tab */}
            <TabPanel>
              {facility.creditEvents && facility.creditEvents.length > 0 ? (
                <Box overflowX='auto'>
                  <Box as='table' w='100%' fontSize='sm'>
                    <Box as='thead'>
                      <Box as='tr'>
                        <HeaderCell>Event Type</HeaderCell>
                        <HeaderCell>Amount</HeaderCell>
                        <HeaderCell>Utilized After</HeaderCell>
                        <HeaderCell>Reserved After</HeaderCell>
                        <HeaderCell>Available After</HeaderCell>
                        <HeaderCell>Description</HeaderCell>
                        <HeaderCell>Created At</HeaderCell>
                      </Box>
                    </Box>
                    <Box as='tbody'>
                      {facility.creditEvents.map((event: CreditEventDto) => (
                        <Box as='tr' key={event.id} _hover={{ bg: 'gray.50' }}>
                          <TableCell>
                            {creditEventTypeLabels[event.eventType as CreditEventType] || event.eventType}
                          </TableCell>
                          <TableCell>{formatAmount(event.eventAmount)}</TableCell>
                          <TableCell>{formatAmount(event.balanceUtilized)}</TableCell>
                          <TableCell>{formatAmount(event.balanceReserved)}</TableCell>
                          <TableCell>{formatAmount(event.balanceAvailable)}</TableCell>
                          <TableCell>{event.description || '---'}</TableCell>
                          <TableCell>{formatDateTime(event.createdAt)}</TableCell>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                </Box>
              ) : (
                <Text fontSize='sm' color='gray.400'>
                  No credit events found
                </Text>
              )}
            </TabPanel>

            {/* Utilizations Tab */}
            <TabPanel>
              <VStack spacing='md' py='md'>
                <Text color='gray.500'>Credit utilizations for this facility</Text>
                <Button
                  size='sm'
                  colorScheme='red'
                  variant='outline'
                  onClick={() => history.push(`/app/bnpl/credit-utilizations?facilityId=${facility.id}`)}>
                  View All Utilizations
                </Button>
              </VStack>
            </TabPanel>

            {/* Statements Tab */}
            <TabPanel>
              <VStack spacing='md' py='md'>
                <Text color='gray.500'>Supplier statements for this facility</Text>
                <Button
                  size='sm'
                  colorScheme='red'
                  variant='outline'
                  onClick={() => history.push(`/app/bnpl/supplier-statements?facilityId=${facility.id}`)}>
                  View All Statements
                </Button>
              </VStack>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </WhiteCard>
    </VStack>
  );
};

const HeaderCell = ({ children }: { children: React.ReactNode }) => (
  <Box
    as='th'
    textAlign='left'
    px='md'
    py='sm'
    borderBottom='1px'
    borderColor='gray.200'
    color='gray.500'
    fontSize='xs'
    fontWeight='semibold'
    textTransform='uppercase'>
    {children}
  </Box>
);

const TableCell = ({ children }: { children: React.ReactNode }) => (
  <Box as='td' px='md' py='sm' borderBottom='1px' borderColor='gray.100'>
    {children}
  </Box>
);
